import { PerspectiveCamera, TrackballControls } from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import useInput from 'lib/hooks/useInput';
import { extractExtension } from 'lib/library';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import * as THREE from 'three';
import ModelFallback from './../ModelFallback';
import ModelOptions from './../ModelOptions';
import { Model } from './../Models';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import MeshPLY from './MeshPLY';
import MeshSTL from './MeshSTL';
import MeshOBJ from './MeshOBJ';
import CameraControls from './CameraControls';
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib';
import ProjectModelViewerErrorBoundary from './ProjectModelViewerErrorBoundary';
import { useProjectFileStore } from 'store/local/projectFile';

interface ProjectModelViewerProps {
  width: number;
  height: number;
  backgroundColor?: string;
  className: string;
  model: any[];
  isFullScreen?: boolean;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<boolean>;
  };
  onLoading?: (isLoading: boolean) => void;
}

const ProjectModelViewer = ({
  width = 450,
  height = 425,
  backgroundColor = '#fcfcfc',
  className = '',
  model = [],
  isFullScreen = false,
  isModelFileLoadingProgress,
  onLoading,
}: ProjectModelViewerProps) => {
  // SECTION: 변수 선언
  const { setModelError } = useProjectFileStore();
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<TrackballControlsImpl | null>(null);
  const groupRef = useRef<THREE.Group | null>(null);
  const cameraAlign = useInput('front');
  const isAnimated = useInput(false);
  const isAxesHelper = useInput(false);
  const isMonoScanView = useInput(false);
  const isViewBoxOpen = useInput(false);
  const isWireframeView = useInput(false);
  const isEnd = useInput(false);

  useEffect(() => {
    if (isEnd.value) {
      if (groupRef.current && controlsRef.current) {
        const box = new THREE.Box3().setFromObject(groupRef.current);
        const center = box.getCenter(new THREE.Vector3());
        // 중심을 원점으로 옮기기 (음수로 이동)
        groupRef.current.position.set(
          groupRef.current.position.x - center.x,
          groupRef.current.position.y - center.y,
          groupRef.current.position.z - center.z,
        );
      }
    }
  }, [isEnd.value, groupRef.current?.children]);

  return (
    <StyledProjectModelViewer
      data-component-name="ProjectModelViewer"
      className={className}
      width={width}
      height={height}
      backgroundColor={backgroundColor}
    >
      <ModelOptions
        groupRef={groupRef}
        cameraAlign={cameraAlign}
        isAnimated={isAnimated}
        isAxesHelper={isAxesHelper}
        isWireframeView={isWireframeView}
        isMonoScanView={isMonoScanView}
        isViewBoxOpen={isViewBoxOpen}
      />
      <Canvas>
        <PerspectiveCamera
          makeDefault
          ref={cameraRef}
          fov={10}
          aspect={window.innerWidth / window.innerHeight}
          position={[0, 500, 0]}
          near={1}
          far={10000}
        />
        <CameraControls
          controlsRef={controlsRef}
          cameraAlign={cameraAlign}
          isAnimated={isAnimated}
        />
        <Suspense fallback={<ModelFallback isEnd={isEnd} onLoading={onLoading} />}>
          <ProjectModelViewerErrorBoundary
            onError={(error: Error) => {
              setModelError(error);
            }}
          >
            <axesHelper scale={1000} visible={isAxesHelper.value} />
            <group ref={groupRef} position={[0, 0, 0]}>
              {useMemo(() => {
                return (
                  model.length > 0 &&
                  model.map((modelFile: IConvertFile) => {
                    const handleModelFile = (mesh: THREE.Mesh) => {
                      if (!mesh) return;

                      modelFile.material = mesh?.material;
                      modelFile.mesh = mesh;
                    };
                    const extension = extractExtension(modelFile.name);

                    if ((modelFile?.fileData || modelFile?.url) && extension === 'ply') {
                      return (
                        <MeshPLY
                          key={modelFile.id}
                          extension={extension}
                          url={modelFile.url}
                          fileData={modelFile.fileData}
                          textureUrl={modelFile.textureUrl}
                          textureFileData={modelFile.textureFileData}
                          isModelFileLoadingProgress={isModelFileLoadingProgress}
                          onModelFile={handleModelFile}
                        />
                      );
                    }
                    if ((modelFile?.fileData || modelFile?.url) && extension === 'stl') {
                      return (
                        <MeshSTL
                          key={modelFile.id}
                          extension={extension}
                          url={modelFile.url}
                          fileData={modelFile.fileData}
                          textureUrl={modelFile.textureUrl}
                          textureFileData={modelFile.textureFileData}
                          isModelFileLoadingProgress={isModelFileLoadingProgress}
                          onModelFile={handleModelFile}
                        />
                      );
                    }
                    if ((modelFile?.fileData || modelFile?.url) && extension === 'obj') {
                      return (
                        <MeshOBJ
                          key={modelFile.id}
                          extension={extension}
                          url={modelFile.url}
                          fileData={modelFile.fileData}
                          textureUrl={modelFile.textureUrl}
                          textureFileData={modelFile.textureFileData}
                          isModelFileLoadingProgress={isModelFileLoadingProgress}
                          onModelFile={handleModelFile}
                        />
                      );
                    }
                  })
                );
              }, [model])}
            </group>
          </ProjectModelViewerErrorBoundary>
        </Suspense>
      </Canvas>
    </StyledProjectModelViewer>
  );
};

const StyledProjectModelViewer = styled.div<{
  width: number;
  height: number;
  backgroundColor: string;
}>`
  position: relative;
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  background-color: ${({ backgroundColor }) => backgroundColor};
  .projectModelViewer__loading {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .projectModelViewer__dim {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    font-size: 14px;
    color: #fff;
    line-height: 1.3;
    text-align: center;
  }
  .projectModelViewer__fit_btn {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 30px;
    color: gray;
    svg {
      font-size: inherit;
    }
  }

  .projectModelViewer__alignManipulator_box {
    position: absolute;
    width: 200px;
    height: 200px;
    /* margin: 20px; */
    left: 30px;
    bottom: 30px;
    .pre {
      &:hover {
        cursor: pointer;
      }
    }
    .up {
      position: absolute;
      left: 50%;
      transform: translate(-50%, 0%);
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .down {
      position: absolute;
      bottom: 0px;
      left: 50%;
      transform: translate(-50%, 0%);
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .left {
      position: absolute;
      left: 0px;
      top: 50%;
      transform: translate(0%, -50%);
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .right {
      position: absolute;
      right: 0px;
      top: 50%;
      transform: translate(0%, -50%);
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .back {
      position: absolute;
      top: 0px;
      right: 0px;
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .front {
      position: absolute;
      bottom: 0px;
      left: 0px;
      .nor {
        display: block;
      }
      .pre {
        display: none;
      }
      &:hover .nor {
        display: none;
      }
      &:hover .pre {
        display: block;
      }
    }
    .center {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

export default ProjectModelViewer;
